// src/pages/HojaVidaEstudiante.jsx

import React, {
  useEffect,
  useMemo,
  useState,
  useCallback
} from "react";

import axios from "axios";

import { API } from "../components/config";

import {
  FaSearch,
  FaUserGraduate,
  FaPlus,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBook,
  FaClipboardList,
  FaSpinner
} from "react-icons/fa";

export default function HojaVidaEstudianteProfesor() {

  /* =====================================================
     TOKEN
  ===================================================== */
  const token = localStorage.getItem("token");

  /* =====================================================
     AXIOS CONFIG
  ===================================================== */
  const axiosConfig = useMemo(() => ({
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${token}`
    }
  }), [token]);

  /* =====================================================
     STATES
  ===================================================== */
  const [busqueda, setBusqueda] =
    useState("");

  const [estudiantes, setEstudiantes] =
    useState([]);

  const [
    estudianteSeleccionado,
    setEstudianteSeleccionado
  ] = useState(null);

  const [hojaVida, setHojaVida] =
    useState(null);

  const [registros, setRegistros] =
    useState([]);

  const [
    loadingEstudiantes,
    setLoadingEstudiantes
  ] = useState(false);

  const [loadingHoja, setLoadingHoja] =
    useState(false);

  const [guardando, setGuardando] =
    useState(false);

  const [filtro, setFiltro] =
    useState("todos");

  const [error, setError] =
    useState("");

  const [nuevoRegistro, setNuevoRegistro] =
    useState({
      tipo: "academico",
      titulo: "",
      descripcion: "",
      nivel: "bajo",
      visible_estudiante: 1
    });

  /* =====================================================
     VALIDAR TOKEN
  ===================================================== */
  useEffect(() => {

    if (!token) {

      localStorage.removeItem("token");

      window.location.href = "/login";
    }

  }, [token]);

  /* =====================================================
     HANDLE ERROR
  ===================================================== */
  const handleAxiosError = useCallback(
    (error) => {

      console.error(
        "❌ Error hoja vida:",
        error
      );

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Error interno";

      setError(message);

      if (
        error?.response?.status === 401
      ) {

        alert(
          "Sesión expirada. Inicia nuevamente."
        );

        localStorage.removeItem(
          "token"
        );

        window.location.href =
          "/login";
      }
    },
    []
  );

  /* =====================================================
     CARGAR ESTUDIANTES
  ===================================================== */
  const fetchEstudiantes =
    useCallback(async () => {

      try {

        setLoadingEstudiantes(true);

        setError("");

        const res = await axios.get(
          `${API}/hoja-vida/mis-estudiantes`,
          axiosConfig
        );

        const data = Array.isArray(
          res.data
        )
          ? res.data
          : [];

        setEstudiantes(data);

      } catch (error) {

        handleAxiosError(error);

      } finally {

        setLoadingEstudiantes(false);
      }
    }, [
      axiosConfig,
      handleAxiosError
    ]);

  useEffect(() => {

    fetchEstudiantes();

  }, [fetchEstudiantes]);

  /* =====================================================
     FILTRAR ESTUDIANTES
  ===================================================== */
  const estudiantesFiltrados =
    useMemo(() => {

      const texto =
        busqueda
          .trim()
          .toLowerCase();

      if (!texto) {
        return estudiantes;
      }

      return estudiantes.filter(
        (e) => {

          const nombre = `
            ${e.nombres_estudiante || ""}
            ${e.apellidos_estudiante || ""}
          `
            .toLowerCase()
            .trim();

          return (
            nombre.includes(texto) ||
            String(
              e.documento_estudiante || ""
            ).includes(texto) ||
            String(
              e.grado || ""
            ).includes(texto) ||
            String(
              e.grupo || ""
            )
              .toLowerCase()
              .includes(texto)
          );
        }
      );

    }, [
      busqueda,
      estudiantes
    ]);

  /* =====================================================
     CARGAR HOJA VIDA
  ===================================================== */
const cargarHojaVida = useCallback(async (matriculaId) => {
  try {
    setLoadingHoja(true);
    setError("");

    const res = await axios.get(
      `${API}/hoja-vida/timeline/${matriculaId}`,
      axiosConfig
    );

    setHojaVida(res.data?.estudiante || null);
    setRegistros(Array.isArray(res.data?.timeline) ? res.data.timeline : []);

  } catch (error) {
    handleAxiosError(error);
  } finally {
    setLoadingHoja(false);
  }
}, [axiosConfig, handleAxiosError]);

  /* =====================================================
     SELECCIONAR ESTUDIANTE
  ===================================================== */
  const seleccionarEstudiante =
    async (estudiante) => {

      setEstudianteSeleccionado(
        estudiante
      );

      await cargarHojaVida(
        estudiante.id
      );
    };

  /* =====================================================
     CREAR REGISTRO
  ===================================================== */
  const crearRegistro =
    async () => {

      try {

        if (!hojaVida) {
          return alert(
            "Selecciona un estudiante"
          );
        }

        if (
          !nuevoRegistro.titulo.trim()
        ) {
          return alert(
            "Escribe un título"
          );
        }

        if (
          !nuevoRegistro.descripcion.trim()
        ) {
          return alert(
            "Escribe una descripción"
          );
        }

        setGuardando(true);

        setError("");

        await axios.post(
          `${API}/hoja-vida/registro`,
          {
            hoja_vida_id:
              hojaVida.id,

            matricula_id:
              hojaVida.matricula_id,

            documento_estudiante:
              hojaVida.documento_estudiante,

            tipo:
              nuevoRegistro.tipo,

            titulo:
              nuevoRegistro.titulo,

            descripcion:
              nuevoRegistro.descripcion,

            nivel:
              nuevoRegistro.nivel,

            visible_estudiante:
              nuevoRegistro.visible_estudiante
          },
          axiosConfig
        );

        setNuevoRegistro({
          tipo: "academico",
          titulo: "",
          descripcion: "",
          nivel: "bajo",
          visible_estudiante: 1
        });

        await cargarHojaVida(
          hojaVida.matricula_id
        );

      } catch (error) {

        handleAxiosError(error);

      } finally {

        setGuardando(false);
      }
    };

  /* =====================================================
     FILTRAR REGISTROS
  ===================================================== */
  const registrosFiltrados =
    useMemo(() => {

      if (filtro === "todos") {
        return registros;
      }

      return registros.filter(
        (r) => r.tipo === filtro
      );

    }, [
      filtro,
      registros
    ]);

  /* =====================================================
     ICONOS
  ===================================================== */
  const getIcono = (tipo) => {

    switch (tipo) {

      case "logro":
        return (
          <FaCheckCircle
            color="green"
          />
        );

      case "disciplinario":
        return (
          <FaExclamationTriangle
            color="red"
          />
        );

      case "academico":
        return (
          <FaBook
            color="#1976d2"
          />
        );

      default:
        return (
          <FaClipboardList
            color="#555"
          />
        );
    }
  };

  /* =====================================================
     RENDER
  ===================================================== */
  return (

    <div style={styles.container}>

      {/* =====================================================
          SIDEBAR
      ===================================================== */}
      <div style={styles.sidebar}>

        <h2 style={styles.title}>
          <FaUserGraduate />
          Hoja de Vida
        </h2>

        {/* SEARCH */}
        <div style={styles.searchBox}>

          <FaSearch />

          <input
            type="text"
            placeholder="Buscar estudiante..."
            value={busqueda}
            onChange={(e) =>
              setBusqueda(
                e.target.value
              )
            }
            style={styles.searchInput}
          />
        </div>

        {/* ERROR */}
        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {/* LOADING */}
        {loadingEstudiantes && (
          <div style={styles.loading}>
            <FaSpinner
              className="spin"
            />
            Cargando estudiantes...
          </div>
        )}

        {/* LISTA */}
        <div style={styles.lista}>

          {!loadingEstudiantes &&
            estudiantesFiltrados
              .length === 0 && (
              <div
                style={styles.emptyList}
              >
                No hay estudiantes
              </div>
            )}

          {estudiantesFiltrados.map(
            (e) => (

              <div
                key={e.id}
                style={{
                  ...styles.estudianteCard,
                  background:
                    estudianteSeleccionado?.id ===
                    e.id
                      ? "#e3f2fd"
                      : "#fff",
                  border:
                    estudianteSeleccionado?.id ===
                    e.id
                      ? "2px solid #1976d2"
                      : "1px solid #ddd"
                }}
                onClick={() =>
                  seleccionarEstudiante(
                    e
                  )
                }
              >

                <strong>
                  {
                    e.nombres_estudiante
                  }{" "}
                  {
                    e.apellidos_estudiante
                  }
                </strong>

                <span>
                  {e.grado}°
                  {" "}
                  {e.grupo}
                </span>

                <small>
                  {
                    e.documento_estudiante
                  }
                </small>

              </div>
            )
          )}
        </div>
      </div>

      {/* =====================================================
          CONTENT
      ===================================================== */}
      <div style={styles.content}>

        {!estudianteSeleccionado && (

          <div style={styles.empty}>
            <h2>
              📘 Hoja de Vida
              Estudiantil
            </h2>

            <p>
              Selecciona un
              estudiante para
              comenzar
            </p>
          </div>
        )}

        {estudianteSeleccionado && (
          <>

            {/* HEADER */}
            <div style={styles.header}>

              <div>

                <h2>
                  {
                    estudianteSeleccionado.nombres_estudiante
                  }{" "}
                  {
                    estudianteSeleccionado.apellidos_estudiante
                  }
                </h2>

                <p>
                  Documento:
                  {" "}
                  {
                    estudianteSeleccionado.documento_estudiante
                  }
                </p>

                <p>
                  {
                    estudianteSeleccionado.grado
                  }
                  °
                  {" "}
                  {
                    estudianteSeleccionado.grupo
                  }
                </p>

              </div>

            </div>

            {/* FORM */}
            <div style={styles.formCard}>

              <h3
                style={styles.formTitle}
              >
                <FaPlus />
                Nueva anotación
              </h3>

              <div style={styles.grid}>

                <select
                  value={
                    nuevoRegistro.tipo
                  }
                  onChange={(e) =>
                    setNuevoRegistro({
                      ...nuevoRegistro,
                      tipo:
                        e.target.value
                    })
                  }
                  style={styles.input}
                >

                  <option value="academico">
                    Académico
                  </option>

                  <option value="disciplinario">
                    Disciplinario
                  </option>

                  <option value="logro">
                    Logro
                  </option>

                  <option value="alerta">
                    Alerta
                  </option>

                  <option value="convivencia">
                    Convivencia
                  </option>

                </select>

                <select
                  value={
                    nuevoRegistro.nivel
                  }
                  onChange={(e) =>
                    setNuevoRegistro({
                      ...nuevoRegistro,
                      nivel:
                        e.target.value
                    })
                  }
                  style={styles.input}
                >

                  <option value="bajo">
                    Bajo
                  </option>

                  <option value="medio">
                    Medio
                  </option>

                  <option value="alto">
                    Alto
                  </option>

                  <option value="critico">
                    Crítico
                  </option>

                </select>

              </div>

              <input
                type="text"
                placeholder="Título"
                value={
                  nuevoRegistro.titulo
                }
                onChange={(e) =>
                  setNuevoRegistro({
                    ...nuevoRegistro,
                    titulo:
                      e.target.value
                  })
                }
                style={styles.input}
              />

              <textarea
                rows={5}
                placeholder="Descripción..."
                value={
                  nuevoRegistro.descripcion
                }
                onChange={(e) =>
                  setNuevoRegistro({
                    ...nuevoRegistro,
                    descripcion:
                      e.target.value
                  })
                }
                style={styles.textarea}
              />

              <button
                onClick={
                  crearRegistro
                }
                disabled={
                  guardando
                }
                style={{
                  ...styles.saveButton,
                  opacity:
                    guardando
                      ? 0.7
                      : 1
                }}
              >

                {guardando
                  ? "Guardando..."
                  : "Guardar anotación"}

              </button>

            </div>

            {/* FILTROS */}
            <div style={styles.filters}>

              {[
                "todos",
                "academico",
                "disciplinario",
                "logro",
                "alerta",
                "convivencia"
              ].map((t) => (

                <button
                  key={t}
                  onClick={() =>
                    setFiltro(t)
                  }
                  style={{
                    ...styles.filterButton,
                    background:
                      filtro === t
                        ? "#1976d2"
                        : "#f1f1f1",
                    color:
                      filtro === t
                        ? "#fff"
                        : "#000"
                  }}
                >
                  {t.toUpperCase()}
                </button>
              ))}

            </div>

            {/* TIMELINE */}
            <div style={styles.timeline}>

              {loadingHoja && (
                <div style={styles.loading}>
                  <FaSpinner />
                  Cargando timeline...
                </div>
              )}

              {!loadingHoja &&
                registrosFiltrados
                  .length === 0 && (

                  <div
                    style={
                      styles.emptyTimeline
                    }
                  >
                    No existen
                    registros
                  </div>
                )}

              {!loadingHoja &&
                registrosFiltrados.map(
                  (r) => (

                    <div
                      key={r.id}
                      style={
                        styles.timelineCard
                      }
                    >

                      <div
                        style={
                          styles.timelineHeader
                        }
                      >

                        <div
                          style={
                            styles.timelineTipo
                          }
                        >

                          {getIcono(
                            r.tipo
                          )}

                          <strong>
                            {String(
                              r.tipo || ""
                            ).toUpperCase()}
                          </strong>

                        </div>

                        <span
                          style={
                            styles.badge
                          }
                        >
                          {r.nivel}
                        </span>

                      </div>

                      <h4>
                        {r.titulo}
                      </h4>

                      <p>
                        {r.descripcion}
                      </p>

                      <small>
                        👨‍🏫{" "}
                        {
                          r.rol_emisor
                        }
                      </small>

                      <br />

                      <small>
                        📅{" "}
                        {new Date(
                          r.creado_en
                        ).toLocaleString()}
                      </small>

                    </div>
                  )
                )}

            </div>

          </>
        )}

      </div>

    </div>
  );
}

/* =====================================================
   STYLES
===================================================== */
const styles = {

  container: {
    display: "flex",
    height: "100vh",
    background: "#f5f7fb"
  },

  sidebar: {
    width: 350,
    background: "#fff",
    borderRight: "1px solid #ddd",
    padding: 20,
    overflowY: "auto"
  },

  title: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20
  },

  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#f1f1f1",
    padding: 12,
    borderRadius: 12,
    marginBottom: 15
  },

  searchInput: {
    border: "none",
    background: "transparent",
    outline: "none",
    width: "100%",
    fontSize: 14
  },

  lista: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  estudianteCard: {
    padding: 15,
    borderRadius: 12,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    transition: "0.2s"
  },

  content: {
    flex: 1,
    padding: 20,
    overflowY: "auto"
  },

  empty: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
  },

  header: {
    background: "#fff",
    padding: 20,
    borderRadius: 16,
    marginBottom: 20
  },

  formCard: {
    background: "#fff",
    padding: 20,
    borderRadius: 16,
    marginBottom: 20
  },

  formTitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20
  },

  grid: {
    display: "grid",
    gridTemplateColumns:
      "1fr 1fr",
    gap: 10
  },

  input: {
    width: "100%",
    padding: 12,
    borderRadius: 10,
    border: "1px solid #ccc",
    marginBottom: 10,
    outline: "none"
  },

  textarea: {
    width: "100%",
    padding: 12,
    borderRadius: 10,
    border: "1px solid #ccc",
    resize: "vertical",
    marginBottom: 10,
    outline: "none"
  },

  saveButton: {
    background: "#1976d2",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: "bold"
  },

  filters: {
    display: "flex",
    gap: 10,
    marginBottom: 20,
    flexWrap: "wrap"
  },

  filterButton: {
    border: "none",
    padding: "10px 15px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: "bold"
  },

  timeline: {
    display: "flex",
    flexDirection: "column",
    gap: 15
  },

  timelineCard: {
    background: "#fff",
    padding: 20,
    borderRadius: 16,
    border: "1px solid #ddd"
  },

  timelineHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10
  },

  timelineTipo: {
    display: "flex",
    alignItems: "center",
    gap: 10
  },

  badge: {
    background: "#f1f1f1",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 12,
    textTransform: "capitalize"
  },

  loading: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 10
  },

  error: {
    background: "#ffebee",
    color: "#c62828",
    padding: 12,
    borderRadius: 10,
    marginBottom: 15
  },

  emptyList: {
    padding: 20,
    textAlign: "center",
    color: "#777"
  },

  emptyTimeline: {
    background: "#fff",
    padding: 30,
    borderRadius: 16,
    textAlign: "center",
    color: "#777"
  }
};